import EmailIcon from "@mui/icons-material/Email";
import { Alert, DialogActions, DialogContent } from "@mui/material";
import {
  Button,
  Checkbox,
  CreatedUpdatedBy,
  Field,
  FieldsContainer,
  Form,
  PageHeader,
  TextField,
} from "common";
import { ParamsContext } from "common/components/Form/ParamsContext";
import HistoryListDialog from "features/histories/HistoryListDialog";
import useAccess from "features/profile/useAccess";
import I18n from "i18n";
import { createContext } from "react";
import useEmailTemplateForm, {
  UseEmailTemplateFormProps,
  UseEmailTemplateFormReturnType,
} from "./useEmailTemplateForm";

export const EmailTemplateContext = createContext<
  UseEmailTemplateFormReturnType | undefined
>(undefined);

export interface EmailTemplateFormProps extends UseEmailTemplateFormProps {}

export default function EmailTemplateForm({
  id,
  afterSave,
  onClose,
  defaultValues,
}: EmailTemplateFormProps) {
  const { hasPermissions, canEditItem } = useAccess();
  const form = useEmailTemplateForm({
    id,
    afterSave,
    defaultValues,
  });
  const {
    params,
    item,
    options,
    errors,
    loading,
    isDirty,
    isCreate,
    isUpdate,
    getIsDirty,
    getRequiredFields,
    onChange,
    save,
    saving,
  } = form;
  const title = isCreate ? I18n.t("EmailTemplate.addTitle") : item.name;
  const subtitle = isCreate ? undefined : undefined;

  return (
    <ParamsContext.Provider value={{ email_template_id: id }}>
      <EmailTemplateContext.Provider value={form}>
        <PageHeader
          avatar={<EmailIcon />}
          title={title}
          subtitle={subtitle}
          onClose={onClose}
          loading={loading}
        />
        <DialogContent>
          <Form
            loading={loading}
            item={item}
            options={options}
            errors={errors}
            onChange={onChange}
            translationCategory="EmailTemplate.attributes"
            translationPlaceholdersCategory="EmailTemplate.placeholders"
            translationHelpersCategory="EmailTemplate.helpers"
            isDirty={getIsDirty}
            requiredFields={getRequiredFields()}
            disabled={!canEditItem("email_template", item)}
          >
            <FieldsContainer>
              <Field fullWidth>
                <TextField id="name" />
              </Field>
              <Field fullWidth>
                <TextField id="subject" />
              </Field>
              <Field fullWidth>
                <TextField id="body" multiline rows={10} />
                <Alert severity="info" sx={{ my: 1 }}>
                  {I18n.t("EmailTemplate.greetingAutoInfo")}
                </Alert>
              </Field>
              <Field fullWidth>
                <TextField id="description" multiline rows={3} />
              </Field>
              <Field fullWidth>
                <Checkbox id="is_active" />
              </Field>
            </FieldsContainer>
          </Form>
        </DialogContent>
        <DialogActions>
          {isUpdate && (
            <CreatedUpdatedBy
              objectName="EmailTemplate"
              item={item}
              sx={{
                p: 1,
                paddingTop: 0,
                paddingBottom: 0.5,
                marginRight: "auto",
              }}
              renderDialog={
                hasPermissions(["history.viewany", "history.viewown"])
                  ? props => <HistoryListDialog {...props} subtitle={title} />
                  : undefined
              }
            />
          )}
          <Button
            title={I18n.t("App.save")}
            disabled={!isDirty}
            onClick={() => save()}
            loading={saving}
          />
        </DialogActions>
      </EmailTemplateContext.Provider>
    </ParamsContext.Provider>
  );
}
